<script setup>
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Eye, EyeOff, Trash2, Check, Pencil } from 'lucide-vue-next'
import { ref, computed } from 'vue'
import { MapSource, MapLayer } from '@/components/map'
import { countVertices } from '@/utils/geometry'

const props = defineProps({
  feature: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  map: {
    type: Object,
    required: true
  },
  multiSelect: {
    type: Boolean,
    default: false
  },
  defaultPreview: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['select', 'toggle', 'delete', 'edit'])

const selectFeature = () => {
  emit('select', props.feature)
}

const deleteFeature = () => {
  emit('delete', props.feature)
}

const showPreview = ref(props.defaultPreview)

const togglePreview = () => {
  showPreview.value = !showPreview.value
}

// Calculate vertex count for this feature
const vertexCount = computed(() => {
  if (props.feature && props.feature.geometry) {
    return countVertices(props.feature.geometry)
  }
  return 0
})
</script>

<template>
  <div
    class="border-b last:border-b-0 transition-all"
    :class="{
      'bg-blue-50 dark:bg-blue-950 border-l-4 border-l-blue-500 border-b-blue-200 dark:border-b-blue-800': isSelected,
      'border-border hover:bg-muted/50': !isSelected
    }"
  >
    <div class="px-3 py-2">
      <!-- Title Row -->
      <div @click="selectFeature" class="flex items-center gap-2 mb-1 cursor-pointer">
        <h4 class="text-sm font-medium" :class="isSelected ? 'font-semibold text-blue-900 dark:text-blue-200' : 'text-foreground'">
          <span v-if="!feature.properties || !feature.properties.name">Drawn ({{ index + 1 }})</span>
          <span v-else>
            {{ feature.properties.name }}
          </span>
        </h4>
      </div>

      <!-- Details -->
      <ul class="text-xs mb-2" :class="isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-muted-foreground'">
        <li v-if="feature.properties && feature.properties.addresstype" class="capitalize">Type: {{ feature.properties.addresstype }}</li>
        <li v-if="feature.geometry && feature.geometry.type">Geometry: {{ feature.geometry.type }}</li>
        <li v-else>Geometry: Unknown</li>
        <li v-if="feature.properties && feature.properties.areaText">Size: {{ feature.properties.areaText }}</li>
        <li v-if="vertexCount > 0">Vertices: {{ vertexCount.toLocaleString() }}</li>
      </ul>

      <!-- Action Buttons Row -->
      <div class="flex gap-2 items-center">
        <!-- Multi-select checkbox -->
        <Checkbox
          v-if="multiSelect"
          :checked="isSelected"
          @update:checked="emit('toggle', feature)"
          class="mr-2"
        />

        <Button
          type="button"
          variant="secondary"
          size="icon"
          @click="togglePreview"
          :class="{'text-primary': showPreview}"
        >
          <EyeOff v-if="!showPreview" class="h-4 w-4" />
          <Eye v-else class="h-4 w-4" />
        </Button>
        <Button
          v-if="feature.properties?.isDrawn || feature.properties?.isBoundary"
          type="button"
          variant="secondary"
          size="icon"
          @click="emit('edit', feature)"
        >
          <Pencil class="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          @click="deleteFeature"
          class="text-red-500 hover:text-red-700"
        >
          <Trash2 class="h-4 w-4" />
        </Button>

        <!-- Single-select button (shown when not in multi-select mode) -->
        <Button
          v-if="!multiSelect"
          type="button"
          :variant="isSelected ? 'default' : 'secondary'"
          size="sm"
          @click="selectFeature"
          :disabled="isSelected"
          class="flex items-center gap-1"
        >
          <Check v-if="isSelected" class="h-4 w-4" />
          {{ isSelected ? 'Selected' : 'Select' }}
        </Button>
      </div>
    </div>

    <!-- Map Preview -->
    <MapSource
      v-if="showPreview && feature"
      :map="map"
      type="geojson"
      :id="`preview-feature-${index}`"
      :data="{
        type: 'FeatureCollection',
        features: [feature]
      }"
      :options="{
        'generateId': true
      }"
    >
      <MapLayer
        :map="map"
        :id="`preview-feature-fill-${index}`"
        type="fill"
        :source="`preview-feature-${index}`"
        :options="{
          'paint': {
            'fill-color': '#059669',
            'fill-opacity': 0.1
          },
          'filter': ['==', '$type', 'Polygon']
        }"
      ></MapLayer>

      <MapLayer
        :map="map"
        :id="`preview-feature-line-${index}`"
        type="line"
        :source="`preview-feature-${index}`"
        :options="{
          'paint': {
            'line-color': '#059669',
            'line-width': 2
          }
        }"
      ></MapLayer>
    </MapSource>
  </div>
</template>
